import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, Send, CheckCircle2, ArrowRight, Sparkles, FileSpreadsheet } from 'lucide-react';

const TEMPLATE_PREVIEW = [
  { id: 'fruit_fiesta',     name: 'Fruit Fiesta',     bg: 'linear-gradient(135deg, #FFE9EE 0%, #FFF4D6 50%, #FFE9EE 100%)', accent: '#FF3B6B', icon: '🍓', tagline: 'A juicy, fruity celebration!' },
  { id: 'bracelet_bonanza', name: 'Bracelet Bonanza', bg: 'linear-gradient(135deg, #F3E8FF 0%, #E0E7FF 50%, #F3E8FF 100%)', accent: '#A855F7', icon: '💜', tagline: 'Beads, bling and big smiles!' },
  { id: 'tshirt_party',     name: 'T-Shirt Party',    bg: 'linear-gradient(135deg, #D1FAE5 0%, #CFFAFE 50%, #D1FAE5 100%)', accent: '#06D6A0', icon: '👕', tagline: 'Print, party, repeat!' },
];

const Step = ({ n, icon: Icon, title, body, accent }) => (
  <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
    <div className="flex items-start gap-3">
      <div
        className="flex-none w-11 h-11 rounded-2xl flex items-center justify-center text-white"
        style={{ background: accent }}
        aria-hidden
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B6B7A]">Step {n}</p>
        <h3 className="text-lg font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{title}</h3>
        <p className="text-sm text-[#6B6B7A] mt-1">{body}</p>
      </div>
    </div>
  </div>
);

const InvitesInfo = () => {
  return (
    <div className="min-h-screen bg-[#FFFDF8]" data-testid="invites-info-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #E0E7FF 0%, #D1FAE5 60%, #CFFAFE 100%)' }}>
        <div className="container mx-auto px-4 py-12 sm:py-16">
          <Link to="/" className="inline-flex items-center text-sm text-[#6B6B7A] hover:text-[#1E1E24] mb-5" data-testid="invites-back-home">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home
          </Link>
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6366F1]"
            >
              <Mail className="w-3 h-3" /> Birthday Invites
            </motion.div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }} data-testid="invites-headline">
              Beautiful invites in <span className="text-[#6366F1]">2 minutes,</span><br /> totally free.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-[#1E1E24] max-w-xl">
              Pick a theme, fill in the party details, paste your guest list, and hit send. We'll deliver via email or SMS,
              track who's opened it, and tally the RSVPs for you in real time.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/my-invites/new">
                <Button className="rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white px-6 h-12" data-testid="invites-cta-create">
                  Create my first invite <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Link to="/my-invites">
                <Button variant="outline" className="rounded-full h-12 px-6" data-testid="invites-cta-list">
                  See my invites
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Templates preview */}
      <section className="container mx-auto px-4 py-14">
        <h2 className="text-3xl font-bold text-[#1E1E24] text-center" style={{ fontFamily: 'Fredoka' }}>Three on-brand themes</h2>
        <p className="text-sm text-[#6B6B7A] text-center mt-1">Each one matches a Creative Birthdays experience.</p>
        <div className="grid sm:grid-cols-3 gap-4 mt-7 max-w-4xl mx-auto" data-testid="invites-templates-preview">
          {TEMPLATE_PREVIEW.map((t) => (
            <div key={t.id} className="rounded-2xl p-6 border-2 transition-all" style={{ background: t.bg, borderColor: 'rgba(255,255,255,0.7)' }}>
              <div className="text-4xl mb-2" aria-hidden>{t.icon}</div>
              <p className="font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{t.name}</p>
              <p className="text-xs text-[#6B6B7A] mt-1">{t.tagline}</p>
              <span className="mt-4 inline-block px-3 py-1 rounded-full text-white text-xs font-semibold" style={{ background: t.accent }}>You're Invited!</span>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-[#E8E5DF]">
        <div className="container mx-auto px-4 py-14">
          <h2 className="text-3xl font-bold text-[#1E1E24] text-center" style={{ fontFamily: 'Fredoka' }}>How it works</h2>
          <p className="text-sm text-[#6B6B7A] text-center mt-1">Four steps, under two minutes from start to send.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-7 max-w-5xl mx-auto">
            <Step n={1} icon={Sparkles}        accent="linear-gradient(135deg, #6366F1 0%, #06D6A0 100%)" title="Pick a theme"
                  body="Three on-brand templates ready to go. Each one matches one of our experiences." />
            <Step n={2} icon={Mail}            accent="linear-gradient(135deg, #06D6A0 0%, #0EA5E9 100%)" title="Fill the details"
                  body="Child name, age, date, time, location, and a personal note. Auto-prefilled from your latest booking when available." />
            <Step n={3} icon={FileSpreadsheet} accent="linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)" title="Add recipients"
                  body="Type names manually, paste a list, or upload a CSV. Email and/or SMS — your call." />
            <Step n={4} icon={Send}            accent="linear-gradient(135deg, #FF3B6B 0%, #FB923C 100%)" title="Send & track"
                  body="See a real-time table of who opened, who RSVP'd yes, and who said no. Resend to non-responders in one click." />
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="container mx-auto px-4 py-14">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-[#1E1E24] text-center" style={{ fontFamily: 'Fredoka' }}>What's included</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-7 text-sm">
            {[
              'Up to 200 recipients per invite',
              'Email + SMS delivery',
              'Real-time open & RSVP tracking',
              'Public RSVP page that matches your theme',
              'CSV paste / file upload for guest lists',
              'Auto-reminder to non-responders 3 days out',
              'Copy & share each guest\'s unique RSVP link',
              'Notifications when someone RSVPs',
            ].map((t) => (
              <p key={t} className="flex items-center gap-2 text-[#1E1E24] bg-white border border-[#E8E5DF] rounded-xl p-3">
                <CheckCircle2 className="w-4 h-4 text-[#06D6A0] flex-none" />
                {t}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Why free */}
      <section className="container mx-auto px-4 pb-14">
        <div className="max-w-2xl mx-auto bg-white rounded-3xl border border-[#E8E5DF] p-7">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Why is it free?</h2>
          <p className="text-sm text-[#1E1E24] mt-3">
            Honestly? Because great parties deserve great invites — and we'd rather you spend on the cake than on a mailer service.
            Sending invitations is bundled into every Creative Birthdays account. No upgrade tier, no hidden fees,
            no per-invite charge. Use it once, use it ten times.
          </p>
          <p className="text-sm text-[#1E1E24] mt-3">
            You only pay when you book a party. Everything that helps you organize one — invites, RSVPs, reminders — is on us.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#1E1E24] text-white">
        <div className="container mx-auto px-4 py-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Fredoka' }}>Ready to send your first invite?</h2>
          <p className="text-sm text-white/75 mt-2">It only takes a couple of minutes — and your guests will land on a beautiful, branded RSVP page.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/my-invites/new">
              <Button className="rounded-full bg-[#6366F1] hover:bg-[#4F46E5] text-white px-7 h-12">Create an invite <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="rounded-full h-12 px-6 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvitesInfo;
